import {
  CopyOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import CardBackPJ from '@portal/assets/img/card-back-pj.png';
import CardFrontPJ from '@portal/assets/img/card-front-pj.png';
import CardBack from '@portal/assets/img/card_back.png';
import CardFront from '@portal/assets/img/card_front.png';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import useReduxState from '@portal/hooks/useReduxState';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import { CardStatusDock } from '@portal/models/enumerators/cards';
import { translate } from '@portal/services/i18n';
import { getEnumDescription } from '@portal/utils/enum';
import { message, Spin } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import './CardFlip.less';

interface FlipCardProps {
  isFlipped: boolean;
  isLoading?: boolean;
  background?: string;
  cardNumber?: string;
  expirationDate?: string;
  holderName?: string;
  cvv?: string;
  type: number;
  status?: CardStatusDock;
}

const CardFlip = ({
  isFlipped,
  isLoading = false,
  cardNumber,
  expirationDate,
  holderName,
  cvv,
  type,
  status,
}: FlipCardProps) => {
  const {
    user: { me },
  } = useReduxState();

  const [showNumber, setShowNumber] = useState(false);
  const [showCvv, setShowCvv] = useState(false);
  const isPF = me?.account?.type === AccountTaxIdentifierType.PERSON;
  const textColor = isPF ? 'white' : 'black';

  const copiedRef = useRef(false);

  useEffect(() => {
    return () => {
      if (copiedRef.current) {
        navigator.clipboard.writeText('');
      }
    };
  }, []);

  const handleCopyCardNumber = () => {
    if (!cardNumber) return;
    navigator.clipboard.writeText(cardNumber).then(() => {
      copiedRef.current = true;
      message.success(
        translate('PAGES.CARDS.MY_CARDS.DETAILS.VIEW_DATA.COPY_DATA_SUCCESS')
      );
    });
  };

  const imageFront = isPF ? `url(${CardFront})` : `url(${CardFrontPJ})`;
  const imageBack = isPF ? `url(${CardBack})` : `url(${CardBackPJ})`;

  const formatCardNumber = (num?: string) => {
    if (!num) return '';
    return num.replace(/(.{4})/g, '$1 ').trim();
  };

  const maskedCardNumber = (num?: string) => {
    if (!num) return '';
    const last4 = num.slice(-4);
    return `●●●● ●●●● ●●●● ${last4}`;
  };

  return (
    <>
      <div
        className={`flip-card ${isFlipped ? 'flipped' : ''}`}
        style={{ opacity: status === CardStatusDock.BLOCKED ? 0.6 : 1 }}
      >
        <div className="flip-card__inner">
          <div
            className="flip-card__front"
            style={{ backgroundImage: imageFront }}
          >
            <div className="flip-card__footer__front">
              <ThemedText
                variant="body"
                className={`flip-card__format__type ${textColor}`}
              >
                {getEnumDescription(type, 'CARD_TYPE_NUMBER')}
              </ThemedText>
            </div>
          </div>
          <div
            className="flip-card__back"
            style={{ backgroundImage: imageBack }}
          >
            <div className="flip-card__content">
              <div className="flip-card__number-row">
                <ThemedText
                  variant="body"
                  className={`flip-card__number ${textColor}`}
                >
                  {showNumber
                    ? formatCardNumber(cardNumber)
                    : maskedCardNumber(cardNumber)}
                </ThemedText>
                <button
                  className={`flip-card__icon-btn ${textColor}`}
                  onClick={() => setShowNumber((v) => !v)}
                >
                  {showNumber ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                </button>
                {cardNumber ? (
                  <button
                    className={`flip-card__icon-btn ${textColor}`}
                    onClick={handleCopyCardNumber}
                    title={translate(
                      'PAGES.CARDS.MY_CARDS.DETAILS.VIEW_DATA.COPY_DATA'
                    )}
                  >
                    <CopyOutlined />
                  </button>
                ) : null}
              </div>
              <div className="flip-card__meta-row">
                <div className="flip-card__meta-item">
                  <ThemedText
                    variant="bodySmall"
                    className={`flip-card__label ${textColor}`}
                  >
                    {translate(
                      'PAGES.CARDS.MY_CARDS.DETAILS.VIEW_DATA.EXPIRATION_DATE'
                    )}
                  </ThemedText>
                  <ThemedText
                    variant="body"
                    className={`flip-card__expiration ${textColor}`}
                  >
                    {expirationDate}
                  </ThemedText>
                </div>
                <div className="flip-card__meta-item">
                  <ThemedText
                    variant="bodySmall"
                    className={`flip-card__label ${textColor}`}
                  >
                    {translate('PAGES.CARDS.MY_CARDS.DETAILS.VIEW_DATA.CVV')}
                  </ThemedText>
                  <div className="flip-card__cvv-row">
                    <ThemedText
                      variant="body"
                      className={`flip-card__cvv ${textColor}`}
                    >
                      {showCvv ? cvv : '●●●'}
                    </ThemedText>
                    <button
                      className={`flip-card__icon-btn ${textColor}`}
                      onClick={() => setShowCvv((v) => !v)}
                    >
                      {showCvv ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flip-card__holder-row">
                <ThemedText
                  variant="bodySmall"
                  className={`flip-card__label ${textColor}`}
                >
                  {translate('PAGES.CARDS.MY_CARDS.DETAILS.VIEW_DATA.HOLDER')}
                </ThemedText>
                <ThemedText
                  variant="body"
                  className={`flip-card__holder ${textColor}`}
                >
                  {holderName}
                </ThemedText>
              </div>
            </div>
          </div>
        </div>
        {isLoading ? (
          <div className="flip-card__loader">
            <Spin indicator={<LoadingOutlined spin />} />
          </div>
        ) : null}
      </div>
    </>
  );
};

export default CardFlip;
